import React, { useEffect, useState } from "react";
import { FileText, DollarSign, User, MessageSquare, Edit2, FileCheck, TrendingUp, CheckCircle, XCircle, Building } from "lucide-react";
import { useNavigate } from "react-router-dom";

const NotesComponent = ({ connectionId, inviteToken,onNavigate  }) => {
  const navigate = useNavigate();
  const [noteData, setNoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [userNote, setUserNote] = useState(""); // New state for user's note
  const token = localStorage.getItem('authToken'); // ✅ Get the token
  const userRole = localStorage.getItem('Role');
  const isAdmin = userRole === 'Admin';
  
  useEffect(() => {
    //console.log("🟢 NotesComponent mounted with connectionId:", connectionId);

    const url = `${process.env.REACT_APP_API_BASE_URL}/notes/${connectionId}`;
    //console.log("📡 Fetching from:", url);

    const fetchNote = async () => {
      try {
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`, // ✅ Include token
          },
        });

        //console.log("📬 Response status:", response.status);
        const text = await response.text();
        //console.log("📦 Raw response:", text);
        const data = JSON.parse(text);
       // console.log("✅ Parsed data:", data);
        setNoteData(data);
      } catch (err) {
        console.error("❌ Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [connectionId]);


  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      const payload = { 
        action, // 'approve', 'reject', or 'edit'
        ...(userNote && { note: userNote }) // Include note if provided
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/notes/${noteData.id}/action`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json",'Authorization': `Bearer ${token}`, },
          
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to update status");

      const data = await response.json();
      //console.log("Updated status:", data);

      // Update local state
      setNoteData(prev => ({ ...prev, status: data.data.status }));

      if (action === "edit") {
        // ✅ Now navigate after saving edit status
        onNavigate(`/admin-config?connectionId=${connectionId}`);
        return;
      }

      // For approve/reject, go back to dashboard
      window.location.href = "/dashboard";

    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    } finally {
      setActionLoading(false);
    }
  };


  const formatCurrency = (value) => (value !== null && value !== undefined ? Number(value).toFixed(2) : "0.00");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading note...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3 text-red-700">
            <div className="text-2xl">⚠️</div>
            <div>
              <h3 className="font-semibold text-lg">Error Loading Note</h3>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!noteData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center text-gray-500">
          <FileText className="w-16 h-16 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium">No note found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
          <div className="flex items-center gap-3 text-white">
            <FileCheck className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Approval Request Details</h2>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-8 space-y-6">
          {/* Request Info Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Replace the company display section with this: */}
            {noteData.company_name && (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="bg-indigo-100 rounded-lg p-2">
                    <Building className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 font-medium mb-1">Company</p>
                    <p className="text-gray-900 font-semibold text-lg">{noteData.company_name}</p>
                    {noteData.company_data?.contact_name && (
                      <p className="text-sm text-gray-600 mt-1">Contact: {noteData.company_data.contact_name}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-lg p-2">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-medium mb-1">Requested By</p>
                  <p className="text-gray-900 font-semibold text-lg">{noteData.requested_by}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-lg p-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-medium mb-1">Request Type</p>
                  <p className="text-gray-900 font-semibold text-lg">{noteData.type}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 rounded-lg p-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-medium mb-1">Discount Amount</p>
                  <p className="text-gray-900 font-semibold text-2xl">£{formatCurrency(noteData.discount_amount)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 rounded-lg p-2">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-medium mb-1">Message</p>
                  <p className="text-gray-900 font-medium">{noteData.message}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Original Note Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-sm text-gray-500 font-semibold uppercase tracking-wide mb-2">Additional Note</h3>
            <p className="text-gray-700 leading-relaxed">{noteData.note || "No note provided"}</p>
          </div>

          {/* New User Note Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-sm text-gray-500 font-semibold uppercase tracking-wide mb-4">Add Your Note</h3>
            <div className="space-y-3">
              <textarea
                value={userNote}
                onChange={(e) => setUserNote(e.target.value)}
                placeholder="Add any comments or notes for this approval request..."
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                disabled={actionLoading}
              />
              <p className="text-sm text-gray-500">
                This note will be saved along with your approval decision.
              </p>
            </div>
          </div>

          {/* Lease Details */}
{/* Lease Details */}
          {noteData.lease_details && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-bold text-gray-800 text-lg">Lease Financial Details</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(noteData.lease_details).map(([key, value]) => {
                    // sales_commission is in "units", not £. profit_percentage is a %.
                    const isCommission = key === 'sales_commission';
                    const isPercentage = key === 'profit_percentage';

                    const displayValue = isCommission
                      ? `${Number(value ?? 0).toFixed(2)} units`
                      : isPercentage
                      ? `${Number(value ?? 0).toFixed(1)}%`
                      : `£${formatCurrency(value)}`;

                    return (
                      <div key={key} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                        <p className="text-xs text-blue-700 font-semibold uppercase tracking-wide mb-1">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-2xl font-bold text-blue-900">{displayValue}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Approve / Reject Buttons */}
          {isAdmin && (
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => handleAction("approve")}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircle className="w-5 h-5" /> Approve
              </button>
              <button
                onClick={() => handleAction("edit")}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 bg-yellow-500 text-white font-semibold px-6 py-3 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Edit2 className="w-5 h-5" /> Edit
              </button>
              
              <button
                onClick={() => handleAction("reject")}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircle className="w-5 h-5" /> Reject
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotesComponent;